import { Map } from 'immutable'
import _toInteger from 'lodash/toInteger'
import _omitBy from 'lodash/omitBy'
import _isUndefined from 'lodash/isUndefined'
import _isString from 'lodash/isString'

/**
 * Wire event names that dual-produce to Brooklyn eventLog (when auth JWT present).
 *
 * Deliberately narrow (see docs/community-analytics-fanout-plan.md): the
 * Braze-segment and funnel events only. Everything else stays PostHog-only.
 */
export const COMMUNITY_EVENT_LOG_EVENTS = [
  'circles_onboarding_shown',
  'circles_onboarding_joined',
  'circles_onboarding_skipped',
  'circles_message_sent',
  'circles_drawer_opened',
  'community_profile_created',
]

const COMMUNITY_EVENT_NAME_MAP = {
  circles_onboarding_shown: 'CirclesOnboardingShown',
  circles_onboarding_joined: 'CirclesOnboardingJoined',
  circles_onboarding_skipped: 'CirclesOnboardingSkipped',
  circles_message_sent: 'CirclesMessageSent',
  circles_drawer_opened: 'CirclesDrawerOpened',
  community_profile_created: 'CommunityProfileCreated',
}

function formatCommunityGaiaEventName (eventName) {
  if (!eventName || !_isString(eventName)) {
    return undefined
  }
  return COMMUNITY_EVENT_NAME_MAP[eventName]
}

function toOptionalInt (value) {
  if (value === undefined || value === null || value === '') {
    return undefined
  }
  return _toInteger(value)
}

function toOptionalBool (value) {
  if (value === undefined || value === null || value === '') {
    return undefined
  }
  return Boolean(value)
}

/**
 * Format enriched Community GA4 payload for Brooklyn eventLog mutation.
 *
 * PRIVACY: mirrors the Community PostHog registry rules — no message content,
 * no other-user identifiers, no display names. Circle ids are backend uuids,
 * not user content.
 *
 * @param {Map} data - Enriched Immutable event from pushCommunityGa4Event
 * @returns {Object} EventLogInput-shaped object
 */
export function formatCommunityEventLogData (data = Map()) {
  if (!Map.isMap(data) || data.isEmpty()) {
    return {}
  }

  const rawEventName = data.get('event') || data.get('eventName')
  const formattedEventName = formatCommunityGaiaEventName(rawEventName)
  if (!formattedEventName) {
    return {}
  }

  const eventData = _omitBy({
    circleId: data.get('circle_id') != null ? String(data.get('circle_id')) : undefined,
    circleIsReply: toOptionalBool(data.get('is_reply')),
    circleCardType: data.get('card_type'),
    circleSecondsSinceLastMessage: toOptionalInt(data.get('seconds_since_last_message')),
    circleRainbowCategoryKey: toOptionalInt(data.get('rainbow_category_key')),
    circleDrawerTrigger: data.get('trigger'),
    profileVisibility: data.get('visibility'),
    uiLocation: data.get('ui_location'),
    uiElement: data.get('ui_element'),
    membershipStatus: data.get('membership_status'),
    userInTest: data.get('user_in_test'),
    userLanguage: data.get('user_language'),
    guestPhase: data.get('guest_phase'),
    appName: data.get('app_name'),
    appVersion: data.get('app_version'),
    allTestsCollated: data.get('all_tests_collated'),
    pageLocation: data.get('current_url'),
    externalId: data.get('gaia_uuid'),
    eventNumber: data.has('event_number') ? _toInteger(data.get('event_number')) : undefined,
  }, _isUndefined)

  return {
    eventName: formattedEventName,
    data: eventData,
  }
}
